// pages/PlatformUserManagement/PlatformUserManagementPage.jsx
//
// Mirrors SchemeManagementPage + MemberManagementPage pattern exactly:
//   ✓ useTableDataWithPost  → replaced with usePlatformUsers (same shape)
//   ✓ useSession            for userid
//   ✓ PageWrapperForm       for layout + add button
//   ✓ DataTable             for listing
//   ✓ DeleteModal           for deactivate confirm
//   ✓ showMainToast         for feedback
//   ✓ useRef                for delete id (fixes async state timing)
//   ✓ flushSync             for edit modal (same as MemberManagementPage)

import React, { useState, useRef } from "react";
import { flushSync } from "react-dom";
import { useTableDataWithPost } from "hooks/useTableWithPost";
import DataTable       from "components/DataTable";
import PageWrapperForm from "components/PageWrapperForm";
import DeleteModal     from "components/DeleteModal";
import { useSession }  from "hooks/useSession";
import { showMainToast } from "utils/showToast";
import { usePlatformUsers,deactivatePlatformUser } from "./PlatformUserAction";
import { PlatformUserManagement,PLATFORM_MODULES, PLATFORM_MANAGER_ROLES } from "./PlatFormConfig";

import PlatformUserFormModal from "./PlatformUserFormModal";
import AuditLogTable from "./AuditLogTable";

// ── Module badges renderer ─────────────────────────────────────────────────
// ── Module badges — rendered in table rows ────────────────────────────────
 
function ModuleBadges({ row }) {
  const active = PLATFORM_MODULES.filter((m) => row[m.key]);
  if (!active.length)
    return <span className="text-muted" style={{ fontSize: 12 }}>None</span>;
  return (
    <div className="d-flex flex-wrap gap-1">
      {active.map((m) => (
        <span key={m.key} className={m.badgeClass} style={{ fontSize: 10 }}>
          <i className={`${m.icon} me-1`} />
          {m.label}
        </span>
      ))}
    </div>
  );
}
 
// ── Page ──────────────────────────────────────────────────────────────────
 
const PlatformUserManagementPage = () => {
  const { userid } = useSession();
  const role       = localStorage.getItem("role") || "";
 
  // ── All hooks before any return ───────────────────────────────────────────
  const [modalShow,    setModalShow]    = useState(false);
  const [modalMode,    setModalMode]    = useState("add");
  const [selectedUser, setSelectedUser] = useState(null);
  const [refresh,      setRefresh]      = useState(0);
  const [activeTab,    setActiveTab]    = useState("users");
  const deleteRef = useRef(null);
 
  // Exactly like MemberManagementPage — FormData → useTableDataWithPost → DataTable
  const formdata = new FormData();
  formdata.append("action", "GET_LIST");
  formdata.append("userid", userid);
 
  const tableData = useTableDataWithPost(
    "https://pro.mokshiq.in/platform_user_action",
    PlatformUserManagement,
    formdata,
    refresh
  );
 
  // ── Guard after all hooks ─────────────────────────────────────────────────
  if (!PLATFORM_MANAGER_ROLES.includes(role)) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: "60vh" }}
      >
        <i className="ph-duotone ph-lock" style={{ fontSize: 48, color: "#adb5bd" }} />
        <h5 className="mt-3 mb-1">Access Restricted</h5>
        <p className="text-muted" style={{ fontSize: 14 }}>
          You don't have permission to manage Platform Users.
        </p>
      </div>
    );
  }
 
  // ── Handlers ──────────────────────────────────────────────────────────────
 
  const handleAddUser = () => {
    setModalMode("add");
    setSelectedUser(null);
    setModalShow(true);
  };
 
  const handleEditUser = (user) => {
    flushSync(() => setSelectedUser(user));
    setModalMode("edit");
    setModalShow(true);
  };
 
  const handleDeactivate = (user) => {
    deleteRef.current = user;
    setTimeout(() => {
      const modalEl = document.getElementById("deactivatePlatformUser");
      if (modalEl) {
        window.bootstrap.Modal.getOrCreateInstance(modalEl).show();
      }
    }, 100);
  };
 
  const handleDeactivateConfirm = async () => {
    const user = deleteRef.current;
    if (!user) return;
    try {
      const res = await deactivatePlatformUser(user.id);
      if (res?.status) {
        showMainToast(res.data || "Platform user deactivated.", "success");
        setRefresh((p) => p + 1);
      } else {
        showMainToast(res?.data || "Failed to deactivate.", "error");
      }
    } catch {
      showMainToast("Server error. Please try again.", "error");
    }
  };
 
  const handleSuccess = () => setRefresh((p) => p + 1);
 
  // ── Render ────────────────────────────────────────────────────────────────
 
  return (
    <div>
      <PageWrapperForm
        config={PlatformUserManagement}
        buttonTitle="Add"
        handleOnclick={handleAddUser}
      >
        {/* Tabs */}
        <div className="px-3 pt-2">
          <ul className="nav nav-tabs border-bottom mb-3">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "users" ? "active" : ""}`}
                onClick={() => setActiveTab("users")}
              >
                <i className="ph-duotone ph-users me-1" />
                Platform Users
                {tableData?.data?.length > 0 && (
                  <span className="badge bg-primary ms-1" style={{ fontSize: 10 }}>
                    {tableData.data.length}
                  </span>
                )}
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "audit" ? "active" : ""}`}
                onClick={() => setActiveTab("audit")}
              >
                <i className="ph-duotone ph-shield-check me-1" />
                Audit Log
              </button>
            </li>
          </ul>
        </div>
 
        {/* Users tab */}
        {activeTab === "users" && (
          <DataTable
            config={PlatformUserManagement}
            tableData={tableData}
            onView={null}
            onEdit={handleEditUser}
            onDelete={handleDeactivate}
          />
        )}
 
        {/* Audit tab */}
        {activeTab === "audit" && (
          <div className="p-3">
            <AuditLogTable refresh={refresh} />
          </div>
        )}
 
      </PageWrapperForm>
 
      {/* Create / Edit modal */}
      <PlatformUserFormModal
        show={modalShow}
        onClose={() => setModalShow(false)}
        mode={modalMode}
        userData={selectedUser}
        onSuccess={handleSuccess}
      />
 
      {/* Deactivate confirm modal */}
      <DeleteModal
        id="deactivatePlatformUser"
        deleteid={deleteRef.current?.id}
        apiUrl="https://pro.mokshiq.in/platform_user_action"
        onSuccess={handleDeactivateConfirm}
      />
    </div>
  );
};
 
export default PlatformUserManagementPage;